/**
 * provider.tsx — React context exposing a single ApiClient instance.
 *
 * The client is wired to the Zustand token store and routes auth-loss back to
 * the auth store so a 401 cascade lands the user on /login.
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { ApiClient } from '@/api/client';
import { tokenStore, useAuthStore } from '@/auth/store';

const ApiContext = createContext<ApiClient | null>(null);

export function ApiProvider({ children }: { children: ReactNode }) {
  const clear = useAuthStore((s) => s.clear);
  const client = useMemo(() => new ApiClient(tokenStore, () => clear()), [clear]);
  return <ApiContext.Provider value={client}>{children}</ApiContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- hook lives with its provider by design
export function useApi(): ApiClient {
  const client = useContext(ApiContext);
  if (!client) throw new Error('useApi must be used within <ApiProvider>');
  return client;
}
